/**
 * The GM's round advance — the round header's NEXT ROUND (`turn-state-on-the-map` 1.5; it was the
 * retired TURN rail's END ROUND). Pure: it reads the state and returns what the button shows and
 * what a press dispatches. The reducer stays the wall; this only mirrors `end_round`'s legality.
 */
import {
  ableUnactedOnSide,
  activationCountsOnSide,
  activeActor,
  type Combatant,
  type EncounterCommand,
  type EncounterState,
} from "@lancer-vtt/domain";

export type RoundFlow = {
  /**
   * Whether a press does something: legal exactly when no *able* un-acted unit remains on either
   * side (the pool `end_round`'s gate reads; the mid-turn unit is not in it). With a unit still
   * mid-turn, a press ends that turn first (see {@link RoundFlow.press}).
   */
  canOpenRound: boolean;
  /** Activations still owed this round, both sides: `remaining + inProgress` per side. */
  toAct: number;
  /** The units still to act — the mid-turn unit first, then LANCERS, then HOSTILES. Raw combatants: a
   *  caller that shows them to a player projects them first. */
  toActUnits: Combatant[];
  /** The one command a press sends, or `null` when there is no actor to send it as. */
  press: () => EncounterCommand | null;
};

/**
 * `actorId` is the viewer's slot (`""` before the socket binds one).
 *
 * The press, and why it is two presses when a unit is mid-turn: this used to compose
 * `end_turn(current) → end_round` in one synchronous burst, safe when `end_turn` was applied
 * optimistically. `end_turn` now rides the `request_end_turn` intent (`decisions/0164` D2) and is not
 * applied optimistically: it may halt server-side for an end-of-turn hazard prompt (System Trauma,
 * the structure-loss interception), so there is no local "it definitely just ended" state to
 * compose on. An `end_round` fired against still-mid-turn local state is rejected by the reducer's
 * dry run ("is mid-turn; end its turn first") and the GM gets no feedback. So a press ends the
 * mid-turn unit's turn alone, and the next press, once the broadcast clears the turn, opens the
 * round. A turn that halts cannot open the round until its prompt is answered anyway.
 */
export function roundFlow(state: EncounterState, actorId: string): RoundFlow {
  const pcUnacted = ableUnactedOnSide(state, "pc");
  const npcUnacted = ableUnactedOnSide(state, "npc");
  const activatingId = activeActor(state.activation);
  const pc = activationCountsOnSide(state, "pc");
  const npc = activationCountsOnSide(state, "npc");
  const acting = activatingId !== null ? state.combatants[activatingId] : undefined;
  return {
    canOpenRound: pcUnacted.length === 0 && npcUnacted.length === 0,
    toAct: pc.remaining + pc.inProgress + npc.remaining + npc.inProgress,
    toActUnits: [...(acting ? [acting] : []), ...pcUnacted, ...npcUnacted],
    press: () => {
      if (!actorId) return null;
      if (activatingId !== null) return { type: "end_turn", actorId, combatantId: activatingId };
      return { type: "end_round", actorId };
    },
  };
}
